import type { SurfaceRenderer } from '../host.js';
import { bootErrorOf } from '../boot-error.js';

/** The error surface's behaviour seam: what trying again does (production re-runs boot). */
export interface ErrorSurfaceDeps {
  retry: () => void | Promise<void>;
}

const defaultDeps: ErrorSurfaceDeps = {
  retry: () => window.location.reload(),
};

let deps: ErrorSurfaceDeps = defaultDeps;

/** Wire the error surface's retry (production re-fetch / dev fixture / tests). */
export function configureErrorSurface(next: Partial<ErrorSurfaceDeps>): void {
  deps = { ...deps, ...next };
}

/**
 * The error surface — the `surface: "error"` branch.
 *
 * Shown when boot failed (a `KernelBootError` with a `stage`), the boot gate reported degraded
 * slices, or the Engine could not be reached. CAOS-662 — it renders the decision's error
 * ENVELOPE, not its raw issues: the code, whose fault it is, whether trying again can help, and
 * the correlation id all come from the normalizer that produced it, rather than being guessed
 * here. The panel draws its own "Try again" only when the failure is retriable; the hand-slotted
 * Retry this used to append had no handler and showed on failures a retry could never fix.
 */
export const errorSurface: SurfaceRenderer<'error'> = {
  surface: 'error',
  render(decision, ctx) {
    const error = bootErrorOf(decision);
    const panel = document.createElement('caos-error-panel');
    panel.setAttribute('heading', "This page couldn't open");
    // CAOS-663 — the boot stage (`org-resolution`, `hydrate`, …) is a machine name nobody reading
    // this can act on, so it is not the eyebrow; the panel's own default is. It is kept as a
    // `data-stage` attribute, where inspecting the page finds it; nothing reads it at runtime.
    panel.setAttribute('data-stage', decision.stage);
    panel.setAttribute('code', error.code);
    panel.setAttribute('fault', error.fault);
    if (error.retriable) panel.setAttribute('retriable', '');
    if (error.correlationId) panel.setAttribute('correlation-id', error.correlationId);
    if (error.docs) panel.setAttribute('docs-href', error.docs);
    panel.append(document.createTextNode(error.message));
    panel.addEventListener('retry', () => {
      void deps.retry();
    });
    ctx.mount.append(panel);
  },
};
